import React, { useState } from 'react';
import { auth } from '../../firebase';
import {
  signInWithGoogle,
  signInWithApple,
  oauthErrorMessage,
} from '../../utils/socialAuth';
import { provisionUserAfterAuth } from '../../utils/provisionUserAfterAuth';

const GoogleIcon = () => (
  <svg width="18" height="18" viewBox="0 0 18 18" aria-hidden focusable="false">
    <path fill="#4285F4" d="M17.64 9.2c0-.64-.06-1.25-.16-1.84H9v3.48h4.84a4.14 4.14 0 0 1-1.8 2.72v2.26h2.92c1.7-1.57 2.68-3.88 2.68-6.62z" />
    <path fill="#34A853" d="M9 18c2.43 0 4.47-.8 5.96-2.18l-2.92-2.26c-.8.54-1.84.86-3.04.86-2.34 0-4.32-1.58-5.02-3.7H.96v2.34A9 9 0 0 0 9 18z" />
    <path fill="#FBBC05" d="M3.98 10.72a5.4 5.4 0 0 1 0-3.44V4.94H.96a9 9 0 0 0 0 8.12l3.02-2.34z" />
    <path fill="#EA4335" d="M9 3.58c1.32 0 2.5.45 3.44 1.35l2.58-2.58C13.47.9 11.43 0 9 0A9 9 0 0 0 .96 4.94l3.02 2.34C4.68 5.16 6.66 3.58 9 3.58z" />
  </svg>
);

const AppleIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden focusable="false">
    <path
      fill="currentColor"
      d="M16.36 12.78c-.02-2.18 1.78-3.23 1.86-3.28-1.01-1.48-2.59-1.69-3.15-1.71-1.34-.14-2.62.79-3.3.79-.68 0-1.73-.77-2.85-.75-1.46.02-2.82.85-3.57 2.16-1.53 2.65-.39 6.56 1.09 8.71.73 1.05 1.59 2.23 2.72 2.19 1.1-.04 1.51-.71 2.84-.71 1.32 0 1.69.71 2.85.69 1.18-.02 1.92-1.07 2.64-2.13.84-1.22 1.18-2.4 1.2-2.46-.03-.01-2.29-.88-2.31-3.49zM14.2 6.06c.6-.73 1-1.74.89-2.75-.86.03-1.91.57-2.53 1.3-.56.64-1.05 1.67-.92 2.65.96.07 1.95-.49 2.56-1.2z"
    />
  </svg>
);

/**
 * "Continue with Google / Apple" buttons + divider, used above the email form in
 * SignUpPopup and LoginPopup. Handles popup sign-in, provisioning, debouncing, and errors.
 *
 * @param {() => boolean} [beforeOAuth] - gate run before sign-in (e.g. terms check). Return false to abort.
 * @param {(info: { isNewUser: boolean }) => void} [onSuccess] - called after a successful sign-in + provisioning.
 * @param {string} [dividerLabel]
 */
export default function OAuthButtons({ beforeOAuth, onSuccess, dividerLabel = 'or continue with email' }) {
  const [busy, setBusy] = useState(null);
  const [error, setError] = useState('');

  const run = async (provider) => {
    if (busy) return;
    setError('');
    if (beforeOAuth && !beforeOAuth()) return;
    setBusy(provider);
    try {
      const result =
        provider === 'google' ? await signInWithGoogle(auth) : await signInWithApple(auth);
      if (!result) return; // redirect started; resolved on next load
      const { isNewUser } = await provisionUserAfterAuth(result, { signupSource: provider });
      onSuccess?.({ isNewUser });
    } catch (err) {
      const msg = oauthErrorMessage(err);
      if (msg) setError(msg);
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="oauth-section">
      <div className="oauth-buttons">
        <button
          type="button"
          className="oauth-button oauth-button--google"
          onClick={() => run('google')}
          disabled={Boolean(busy)}
        >
          <span className="oauth-button-icon">
            <GoogleIcon />
          </span>
          <span className="oauth-button-label">
            {busy === 'google' ? 'Connecting\u2026' : 'Continue with Google'}
          </span>
        </button>
        <button
          type="button"
          className="oauth-button oauth-button--apple"
          onClick={() => run('apple')}
          disabled={Boolean(busy)}
        >
          <span className="oauth-button-icon">
            <AppleIcon />
          </span>
          <span className="oauth-button-label">
            {busy === 'apple' ? 'Connecting\u2026' : 'Continue with Apple'}
          </span>
        </button>
      </div>
      {error && <div className="oauth-error">{error}</div>}
      <div className="oauth-divider">
        <span>{dividerLabel}</span>
      </div>
    </div>
  );
}
